import type { NdjsonEvent, ToolCall } from "./ndjsonTypes";
import { parseNdjsonLine, applyNdjsonEvent } from "./ndjsonParser";
import { AiChatError } from "./AiChatError";

export interface AiChatStreamCallbacks {
  onChunk?: (fullText: string, toolCalls: ToolCall[]) => void;
  onToolCallStart?: (name: string, toolCalls: ToolCall[]) => void;
  onToolCallEnd?: (name: string, toolCalls: ToolCall[]) => void;
  onComplete?: (fullText: string, toolCalls: ToolCall[]) => void;
  onError?: (error: Error) => void;
  onEventType?: Record<string, (data: unknown) => void>;
}

export interface AiChatStreamOverrides {
  llmModel?: {
    llmService: string;
    modelName: string;
  };
  temperature?: number;
  systemPrompt?: string;
}

export interface AiChatStreamConfig extends AiChatStreamCallbacks {
  url: string;
  body: Record<string, unknown>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  overrides?: AiChatStreamOverrides;
  loggingEnabled?: boolean;
}

const normalizeSseType = (type: string): string => type.replace(/\./g, "_");

const parseSseToEvent = (
  eventType: string,
  dataLines: string[]
): NdjsonEvent | null => {
  const data = dataLines.join("\n");
  const canonical = normalizeSseType(eventType);

  if (canonical === "text" || canonical === "content") {
    return { type: "content", data };
  }

  if (canonical === "tool_call_start" || canonical === "tool_call_end") {
    try {
      const parsed = JSON.parse(data) as Record<string, unknown>;
      if (canonical === "tool_call_start") {
        return {
          type: "tool_call_start",
          name: parsed.name as string,
          arguments: parsed.arguments as Record<string, unknown> | undefined
        };
      }
      return {
        type: "tool_call_end",
        name: parsed.name as string,
        result: (parsed.result as string | undefined) ?? ""
      };
    } catch {
      return null;
    }
  }

  return null;
};

export class AiChatStream {
  private config: AiChatStreamConfig;

  constructor(config: AiChatStreamConfig) {
    this.config = config;
    this.log("constructed", {
      url: config.url,
      hasOverrides: !!config.overrides
    });
  }

  private log(...args: unknown[]): void {
    if (!this.config.loggingEnabled) {
      return;
    }
    console.log("[AiChatStream]", ...args);
  }

  private dispatchEventTypeHook(type: string, raw: unknown): void {
    const hooks = this.config.onEventType;
    if (!hooks?.[type]) {
      return;
    }

    let data = raw;
    if (typeof data === "string") {
      try {
        data = JSON.parse(data);
      } catch {
        // keep as string
      }
    }
    this.log("eventTypeHook", type, data);
    hooks[type](data);
  }

  private dispatchNdjsonHook(line: string): void {
    if (!this.config.onEventType) {
      return;
    }
    try {
      const parsed = JSON.parse(line.trim()) as Record<string, unknown>;
      if (typeof parsed.type === "string") {
        this.dispatchEventTypeHook(parsed.type, parsed);
      }
    } catch {
      // ignore unparseable lines
    }
  }

  async start(): Promise<{ fullText: string; toolCalls: ToolCall[] }> {
    const { url, body, headers, signal, overrides } = this.config;

    const requestBody = overrides ? { ...body, overrides } : body;

    this.log("fetch start", url, { overrides, bodyKeys: Object.keys(body) });

    let response: Response;
    try {
      response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          ...headers
        },
        body: JSON.stringify(requestBody),
        signal
      });
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") {
        throw e;
      }
      const error = AiChatError.fromNetworkError(
        e instanceof Error ? e : new Error(String(e))
      );
      this.log("error: network", error.message);
      this.config.onError?.(error);
      throw error;
    }

    this.log("fetch response", {
      status: response.status,
      ok: response.ok,
      contentType: response.headers.get("content-type")
    });

    if (!response.ok) {
      const error = AiChatError.fromHttpStatus(response.status);
      this.log("error: request failed", response.status);
      this.config.onError?.(error);
      throw error;
    }
    if (!response.body) {
      const error = new AiChatError("no_body", "No response body");
      this.log("error: no response body");
      this.config.onError?.(error);
      throw error;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullText = "";
    let buffer = "";
    const toolCalls: ToolCall[] = [];
    let chunkIndex = 0;

    let sseEventType = "";
    let sseDataLines: string[] = [];

    const flushSseEvent = (): void => {
      if (!sseEventType && sseDataLines.length === 0) {
        return;
      }

      this.log("flush SSE event", {
        type: sseEventType,
        dataLineCount: sseDataLines.length,
        data: sseDataLines.join("\n").slice(0, 200)
      });

      if (sseEventType) {
        this.dispatchEventTypeHook(sseEventType, sseDataLines.join("\n"));
      }
      const event = parseSseToEvent(sseEventType, sseDataLines);
      this.log("parsed SSE→event", event?.type ?? "null");
      if (event) {
        fullText = this.applyEvent(event, fullText, toolCalls);
      }
      sseEventType = "";
      sseDataLines = [];
    };

    this.log("starting read loop");

    try {
      // eslint-disable-next-line no-constant-condition, @typescript-eslint/no-unnecessary-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          this.log("reader done");
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        this.log(`chunk #${chunkIndex++}`, {
          bytes: value.byteLength,
          preview: chunk.slice(0, 120)
        });

        buffer += chunk;
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          // NDJSON: line starts with `{`
          if (line.trimStart().startsWith("{")) {
            flushSseEvent();
            this.dispatchNdjsonHook(line);
            const event = parseNdjsonLine(line);
            this.log(
              "NDJSON line→event",
              event?.type ?? "null",
              line.slice(0, 120)
            );
            if (event) {
              fullText = this.applyEvent(event, fullText, toolCalls);
            }
            continue;
          }

          // SSE: empty line = end of event
          if (line.trim() === "") {
            flushSseEvent();
            continue;
          }

          // SSE: event type
          if (line.startsWith("event:")) {
            sseEventType = line.slice(6).trim();
            this.log("SSE event type:", sseEventType);
            continue;
          }

          // SSE: data line
          if (line.startsWith("data:")) {
            sseDataLines.push(line.slice(5));
            continue;
          }

          this.log("unrecognized line:", line.slice(0, 120));
        }
      }

      // flush any remaining buffered content
      if (buffer.trimStart().startsWith("{")) {
        this.log("flushing remaining NDJSON buffer", buffer.slice(0, 120));
        this.dispatchNdjsonHook(buffer);
        const event = parseNdjsonLine(buffer);
        if (event) {
          fullText = this.applyEvent(event, fullText, toolCalls);
        }
      } else if (buffer.startsWith("data:")) {
        sseDataLines.push(buffer.slice(5));
      }
      flushSseEvent();

      this.log("complete", {
        textLength: fullText.length,
        toolCallCount: toolCalls.length
      });
      this.config.onComplete?.(fullText, toolCalls);
      return { fullText, toolCalls };
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") {
        this.log("aborted");
        throw e;
      }
      if (e instanceof AiChatError) {
        throw e;
      }
      const error = new AiChatError(
        "stream_error",
        e instanceof Error ? e.message : String(e)
      );
      this.log("error during stream", error.message);
      this.config.onError?.(error);
      throw error;
    }
  }

  private applyEvent(
    event: NdjsonEvent,
    fullText: string,
    toolCalls: ToolCall[]
  ): string {
    const updatedText = applyNdjsonEvent(event, fullText, toolCalls);

    if (event.type === "tool_call_start") {
      this.log("callback: onToolCallStart", event.name);
      this.config.onToolCallStart?.(event.name, toolCalls);
    } else if (event.type === "tool_call_end") {
      this.log("callback: onToolCallEnd", event.name);
      this.config.onToolCallEnd?.(event.name, toolCalls);
    }

    this.log("callback: onChunk", {
      textLength: updatedText.length,
      toolCallCount: toolCalls.length
    });
    this.config.onChunk?.(updatedText, toolCalls);
    return updatedText;
  }
}
